import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { workflowSetCondition } from '~/actions/workflow';
import ConditionExpressionBuilder from '~/components/Shared/ConditionExpressionBuilder/ConditionExpressionBuilder';
import {
  getInvalidFieldNodes,
  hasLoops,
  testEmptyExpression,
  testHasEmptySections,
} from '~/components/Shared/ConditionExpressionBuilder/utils';
import Links from '~/components/Workflow/shared/Links';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { getAssignedFields } from '~/reducers/workflow';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import InputLabel from '~/ui/components/InputLabel/InputLabel';
import Text from '~/ui/components/Text/Text';
import TextInput from '~/ui/components/TextInput/TextInput';
import { useToggle } from '~/ui/hooks/useToggle';

import fieldDefinitions from './fieldDefinitions';
import getOperatorDefinitions from './operatorDefinitions';

// TODO: find a unified way to validate input errors for UI input components.
function validate(formData, fields, conditions, intl) {
  const errors = [];
  const { hasInvalidFields, expression, name } = formData;

  const loops = hasLoops(fields, conditions);
  if (Object.values(loops).some((hasLoop) => hasLoop)) {
    errors.push(
      intl.formatMessage({
        id: 'ConditionModal.infiniteLoopsError',
        defaultMessage: 'Expression contains infinite loops',
        description: 'Error message for infinite loops in expression',
      }),
    );
  }
  if (!name) {
    errors.push(
      intl.formatMessage({
        id: 'ConditionModal.nameRequiredError',
        defaultMessage: 'Name must be provided',
        description: 'Error message when condition name is required',
      }),
    );
  }
  if (testEmptyExpression(expression)) {
    errors.push(
      intl.formatMessage({
        id: 'ConditionModal.emptyExpressionError',
        defaultMessage: 'Expression must be non-empty',
        description: 'Error message for empty expression',
      }),
    );
  } else if (hasInvalidFields) {
    errors.push(
      intl.formatMessage({
        id: 'ConditionModal.fieldsOperatorsError',
        defaultMessage: 'All fields and operators must be specified',
        description:
          'Error message when fields and operators are not specified',
      }),
    );
  } else if (testHasEmptySections(expression)) {
    errors.push(
      intl.formatMessage({
        id: 'ConditionModal.emptySectionsError',
        defaultMessage: 'Expression must not contain empty sections',
        description: 'Error message for empty sections in expression',
      }),
    );
  }

  return errors;
}

// format fields compatible for ExpressionBuilder
function formatFields(fields) {
  return Object.values(fields).reduce((acc, field) => {
    acc[field.id] = {
      id: field.id,
      name: field.name,
      type: field.type,
      options: field?.customSettings?.options || [],
    };
    return acc;
  }, {});
}

function ConditionModal({
  condition,
  visible,
  onHide,
  // connected
  conditions,
  fields,
  workflowSetCondition,
}) {
  const shouldCreate = !condition;
  const id = condition?.id || uuid();
  const initialName = condition?.name || '';
  const initialExpression = condition?.expression || [];

  const [name, setName] = useState(initialName);
  const [expression, setExpression] = useState(initialExpression);
  const [hasInvalidFields, setHasInvalidFields] = useState(initialExpression);
  const [showLinks, toggleShowLinks] = useToggle(false);
  const intl = useIntl();
  const operatorDefinitions = getOperatorDefinitions(intl);

  function clear() {
    setExpression(initialExpression);
    setHasInvalidFields(false);
    setName(initialName);
  }

  useEffect(
    () => {
      clear();
      if (condition) {
        setName(condition.name);
        setExpression(condition.expression);
      }
    },
    [condition], // eslint-disable-line react-hooks/exhaustive-deps
  );

  function handleHide() {
    clear();
    onHide();
  }

  function handleNameChange(updatedName) {
    setName(updatedName);
  }

  function handleExpressionChange(updatedExpression, updatedTree) {
    setExpression(updatedExpression);
    setHasInvalidFields(getInvalidFieldNodes(updatedTree).length > 0);
  }

  const formData = { hasInvalidFields, name, expression };
  const errors = validate(formData, fields, conditions, intl);
  const hasErrors = errors.length > 0;

  let LinksWarning;
  if (condition?.links) {
    const length = condition?.links.length;
    LinksWarning = length > 0 && (
      <FlexLayout bg="yellow-100" flexDirection="column" p={6} space={6}>
        <FlexLayout alignItems="center" justifyContent="space-between">
          <Text variant="xs-dense-bold">
            {intl.formatMessage(
              {
                id: 'ConditionModal.linkedObjectsModifyWarning',
                defaultMessage:
                  'This condition is linked to {count, plural, one {# object} other {# objects}}. Modifying it will affect the workflow.',
                description: 'Warning message for modifying linked condition',
              },
              { count: length },
            )}
          </Text>
          <Text
            color="peach-800"
            variant="xs-dense-bold"
            onClick={toggleShowLinks}
          >
            {showLinks
              ? intl.formatMessage({
                  id: 'ConditionModal.hideAffectedObjects',
                  defaultMessage: 'Hide affected objects',
                  description: 'Text to hide affected objects',
                })
              : intl.formatMessage({
                  id: 'ConditionModal.showAffectedObjects',
                  defaultMessage: 'Show affected objects',
                  description: 'Text to show affected objects',
                })}
          </Text>
        </FlexLayout>
        {showLinks && <Links bg="yellow-200" links={condition.links} />}
      </FlexLayout>
    );
  }

  return (
    <Modal
      disableHideOnEscape
      disableHideOnClickOutside
      primaryAction={{
        disabled: hasErrors,
        text: shouldCreate
          ? intl.formatMessage({
              id: 'ConditionModal.addConditionButton',
              defaultMessage: 'Add Condition',
              description: 'Button text for adding condition',
            })
          : intl.formatMessage({
              id: 'ConditionModal.saveButton',
              defaultMessage: 'Save',
              description: 'Button text for saving condition',
            }),
        tooltip: hasErrors
          ? errors.map((error) => (
              <React.Fragment key={error}>
                {error}
                <br />
              </React.Fragment>
            ))
          : undefined,
        onClick: () => {
          workflowSetCondition({ id, name, expression });
          handleHide();
        },
      }}
      title={
        shouldCreate
          ? intl.formatMessage({
              id: 'ConditionModal.addConditionModalTitle',
              defaultMessage: 'Add Condition',
              description: 'Modal title for adding a condition',
            })
          : intl.formatMessage({
              id: 'ConditionModal.editConditionModalTitle',
              defaultMessage: 'Edit Condition',
              description: 'Modal title for editing a condition',
            })
      }
      isVisible={visible}
      width="l"
      onHide={handleHide}
    >
      <FlexLayout flexDirection="column" pb={60} space={6}>
        <InputLabel
          isRequired
          id="condition-name"
          placeholder={intl.formatMessage({
            id: 'ConditionModal.namePlaceholder',
            defaultMessage: 'Enter condition name',
            description: 'Placeholder for condition name input',
          })}
          label={intl.formatMessage({
            id: 'ConditionModal.nameLabel',
            defaultMessage: 'Name',
            description: 'Label for condition name',
          })}
        >
          <TextInput
            autoFocus
            value={name}
            width="fullWidth"
            onChange={handleNameChange}
          />
        </InputLabel>
        <FlexLayout flexDirection="column" space={1}>
          <Text color="gray-700" variant="s-dense-bold">
            {intl.formatMessage({
              id: 'ConditionModal.expressionLabel',
              defaultMessage: 'Expression',
              description: 'Label for condition expression',
            })}
          </Text>
          {expression.length === 0 && (
            <Text color="gray-600" variant="xs-dense-medium">
              {intl.formatMessage({
                id: 'ConditionModal.noRulesMessage',
                defaultMessage: 'You have not added any rules',
                description: 'Message when no rules have been added',
              })}
            </Text>
          )}
        </FlexLayout>
        <ConditionExpressionBuilder
          expression={expression}
          fields={formatFields(fields)}
          fieldDefinitions={fieldDefinitions}
          operatorDefinitions={operatorDefinitions}
          onChange={handleExpressionChange}
        />
        {LinksWarning}
      </FlexLayout>
    </Modal>
  );
}

const mapStateToProps = ({ workflow }) => ({
  conditions: workflow.conditions,
  fields: getAssignedFields(workflow, true),
});

export default connect(mapStateToProps, { workflowSetCondition })(
  ConditionModal,
);
